import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ackAlarm, fetchDetail, snoozeAlarm } from "./api.ts";
import {
  STATUS_LABEL, TIME_RANGES,
  type AppDetail, type MetricTile, type TimeRange, type Vital,
} from "./types.ts";
import { Chart } from "./components/Chart.tsx";
import { KorrusMark } from "./components/Logo.tsx";
import { goHome } from "./useRoute.ts";

const SC: Record<string, string> = { ok: "sc-ok", warn: "sc-warn", crit: "sc-crit", unknown: "sc-unknown" };
const DEP_DOT: Record<string, string> = { ok: "var(--ok)", warn: "var(--amber)", crit: "var(--red)", unknown: "var(--grey)" };
const hms = (ms: number) => new Date(ms).toLocaleTimeString(undefined, { hour12: false });
const hm = (iso?: string) => (iso ? new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false }) : "—");

function useClock(): number {
  const [t, setT] = useState(() => Date.now());
  useEffect(() => { const i = setInterval(() => setT(Date.now()), 1000); return () => clearInterval(i); }, []);
  return t;
}

function ChartCard({ tile }: { tile: MetricTile }) {
  const bad = tile.intent === "bad" || tile.intent === "warn";
  return (
    <div className={`ccard${bad ? " bad" : ""}`}>
      <span className="accentbar" />
      <div className="ccard-head">
        <span className="ccard-title">{tile.label}</span>
        <span className="ccard-val">{tile.value}</span>
      </div>
      <Chart series={tile.series} timestamps={tile.timestamps} unit={tile.unit} intent={tile.intent} />
    </div>
  );
}

export function DetailPage({ id }: { id: string }) {
  const [range, setRange] = useState<TimeRange>("3h");
  const [busy, setBusy] = useState(false);
  const clock = useClock();
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["detail", id, range],
    queryFn: () => fetchDetail(id, range),
    refetchInterval: 60_000,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["detail", id] });
    qc.invalidateQueries({ queryKey: ["overview"] });
  };
  const firing = (data?.alarms ?? []).filter((a) => a.state === "ALARM" && !a.snoozedUntil);
  const openFiring = firing.filter((a) => !a.acked);
  const ackAll = async () => {
    if (openFiring.length === 0) return;
    setBusy(true);
    try { await Promise.all(openFiring.map((a) => ackAlarm(a.name))); refresh(); }
    finally { setBusy(false); }
  };
  const snooze = async (name: string) => {
    setBusy(true);
    try { await snoozeAlarm(name, 4); refresh(); }
    finally { setBusy(false); }
  };

  const sc = data ? SC[data.status] : "sc-unknown";
  const vitalTiles: MetricTile[] = (data?.vitals ?? []).map((v: Vital) => ({
    label: v.label, value: v.value, series: v.series, timestamps: v.timestamps,
  }));

  // 24h tick labels for the alarm timeline footer.
  const ticks = Array.from({ length: 5 }, (_, i) => {
    const d = new Date(clock - (4 - i) * 6 * 3600_000);
    return `${`${d.getHours()}`.padStart(2, "0")}:${`${d.getMinutes()}`.padStart(2, "0")}`;
  });

  return (
    <>
      <div className="topbar">
        <button className="back" onClick={goHome} aria-label="Back to fleet">←</button>
        <div className="brand"><KorrusMark /><span className="status-word">status</span></div>
        <div className="breadcrumb"><span className="sep">/</span> Fleet <span className="sep">/</span> <span className="cur">{id}</span></div>
        <div className="spacer" />
        <div className="tabs mono" role="tablist" aria-label="Time range">
          {TIME_RANGES.map((r) => (
            <button key={r} className={range === r ? "on" : ""} role="tab" aria-selected={range === r} onClick={() => setRange(r)}>{r}</button>
          ))}
        </div>
        <span className="clock"><span className="live-dot" />{hms(clock)}</span>
      </div>

      <main>
        {isLoading && <div className="state-msg">Loading telemetry…</div>}
        {isError && <div className="state-msg">Couldn’t load this service’s metrics.</div>}

        {data && (
          <>
            <div className={`hero ${sc}`}>
              <span className="dot" />
              <h1>{data.displayName}</h1>
              <span className="tag tag-type" style={{ fontSize: 10.5 }}>{data.type}</span>
              <span className={`tag ${data.env === "prod" ? "tag-prod" : "tag-dev"}`}>{data.env}</span>
              <span className="status-word">{STATUS_LABEL[data.status]}</span>
              <span className="spacer" />
              <div className="hero-stats">
                <div className="hstat"><div className="l">Uptime 24h</div><div className="v" style={{ color: data.uptime24h == null ? "var(--fg-disabled)" : data.uptime24h >= 99.9 ? "var(--ok)" : data.uptime24h >= 99 ? "var(--amber)" : "var(--red)" }}>{data.uptime24h == null ? "—" : `${data.uptime24h.toFixed(1)}%`}</div></div>
                <div className="hstat"><div className="l">Alarms</div><div className="v" style={{ color: data.alarmCount ? "var(--red)" : "var(--fg-2)" }}>{data.alarmCount} / {data.alarms.length}</div></div>
                <div className="hstat"><div className="l">Owner</div><div className="v prose">{data.owner ?? "—"}</div></div>
                <div className="hstat"><div className="l">$ / day</div><div className="v" style={{ color: data.costPerDay == null ? "var(--fg-disabled)" : "var(--fg-2)" }}>{data.costPerDay == null ? "—" : `$${data.costPerDay.toFixed(2)}`}</div></div>
                <button className="btn" onClick={ackAll} disabled={busy || openFiring.length === 0}>
                  {firing.length > 0 && openFiring.length === 0 ? "Acked ✓" : "Acknowledge"}
                </button>
              </div>
            </div>

            {data.dependencies && data.dependencies.length > 0 && (
              <>
                <div className="sechead">
                  <h2>Dependencies</h2>
                  <span style={{ fontSize: 11.5, color: "var(--fg-3)" }}>account {accountFor(data)} · {regionFor(data)}</span>
                  <span className="rule" />
                </div>
                <div className="deps">
                  {data.dependencies.map((d, i) => (
                    <div key={d.name} style={{ display: "flex", alignItems: "center" }}>
                      <div className={`dep-node ${d.status === "crit" ? "crit" : d.status === "warn" ? "warn" : ""}`}>
                        <div className="top"><span className="d" style={{ background: DEP_DOT[d.status] }} /><span className="nm">{d.name}</span></div>
                        <div className="sub">{d.sub}</div>
                      </div>
                      {i < data.dependencies!.length - 1 && <span className="dep-arrow">→</span>}
                    </div>
                  ))}
                </div>
              </>
            )}

            {data.sections?.map((s) => (
              <div className="dsec" key={s.key}>
                <div className="sechead">
                  <h2>{s.title}</h2>
                  {s.subtitle && <span className="kpi">{s.subtitle}</span>}
                  <span className="rule" />
                </div>
                <div className="ccards">
                  {s.tiles.map((t) => <ChartCard key={t.label} tile={t} />)}
                </div>
              </div>
            ))}

            {data.endpoints && data.endpoints.length > 0 && (
              <div className="dsec">
                <div className="sechead">
                  <h2>Top API Endpoints</h2>
                  <span style={{ fontSize: 11.5, color: "var(--fg-3)" }}>last ~2h · by request volume · from access logs</span>
                  <span className="rule" />
                </div>
                <div className="ep-table">
                  <div className="ep-grid ep-head">
                    <span>Method</span><span>Path</span>
                    <span className="r">Requests</span><span className="r">p95</span><span className="r">Errors</span>
                  </div>
                  {data.endpoints.map((e) => (
                    <div className="ep-grid ep-row" key={`${e.method} ${e.path}`}>
                      <span><span className={`tag ep-method m-${e.method.toLowerCase()}`}>{e.method}</span></span>
                      <span className="ep-path mono">{e.path}</span>
                      <span className="r mono">{e.count.toLocaleString()}</span>
                      <span className="r mono">{e.p95}ms</span>
                      <span className="r mono" style={{ color: e.errorRate > 0 ? "var(--red)" : "var(--fg-disabled)" }}>{e.errorRate.toFixed(1)}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!data.sections && vitalTiles.length > 0 && (
              <div className="dsec">
                <div className="sechead"><h2>Vitals</h2><span className="rule" /></div>
                <div className="ccards">{vitalTiles.map((t) => <ChartCard key={t.label} tile={t} />)}</div>
              </div>
            )}

            <div className="sechead">
              <h2>Alarm timeline</h2>
              <span style={{ fontSize: 11.5, color: "var(--fg-3)" }}>last 24 hours · red = firing</span>
              <span className="rule" />
            </div>
            <div className="timeline">
              {data.alarms.length === 0 ? (
                <div className="state-msg" style={{ padding: "20px 0" }}>No alarms configured for this service.</div>
              ) : (
                <>
                  {data.alarms.map((a) => (
                    <div className="lane" key={a.name}>
                      <span className="nm">
                        {a.name}
                        {a.acked && <span className="albadge acked">acked</span>}
                        {a.snoozedUntil && <span className="albadge snoozed">snoozed</span>}
                      </span>
                      <span className="track">
                        {a.spans?.map((s, i) => (
                          <span key={i} className="span" style={{ left: `${s.l}%`, width: `${Math.max(0.4, s.w)}%` }} />
                        ))}
                      </span>
                      <span className="st" style={{ color: a.snoozedUntil ? "var(--fg-3)" : a.state === "ALARM" ? "var(--red)" : a.state === "OK" ? "var(--ok)" : "var(--amber)" }}>
                        {a.snoozedUntil ? "SNOOZED" : a.state}
                      </span>
                      <span className="since">
                        {a.state === "ALARM" && !a.snoozedUntil
                          ? <button className="mini-btn" disabled={busy} onClick={() => snooze(a.name)}>Snooze 4h</button>
                          : a.state === "ALARM" ? hm(a.since) : "—"}
                      </span>
                    </div>
                  ))}
                  <div className="tl-foot">{ticks.map((t, i) => <span key={i}>{t}</span>)}</div>
                </>
              )}
            </div>
          </>
        )}
      </main>
    </>
  );
}

function accountFor(d: AppDetail): string {
  return d.env === "prod" ? "204107530524" : "990858070417";
}
function regionFor(d: AppDetail): string {
  return d.env === "prod" ? "us-west-1" : "us-west-2";
}
